import { useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { SignIn, SignUp, useUser } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { Shield, Loader2 } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { STRIPE_TIERS, type StripeTierKey } from "@/config/stripeTiers";
import { isBundleKey, BUNDLE_BY_KEY, type BundleKey } from "@/config/bundles";

type SignupPlan = StripeTierKey | "free" | BundleKey;

const parseSignupPlan = (value: string | null): SignupPlan | null => {
  if (value === "free" || value === "basic" || value === "pro" || value === "enterprise") {
    return value;
  }
  return isBundleKey(value) ? value : null;
};

/** A paid selection worth carrying through onboarding to checkout. */
const isPaidPlan = (plan: SignupPlan | null): boolean =>
  plan === "basic" || plan === "pro" || isBundleKey(plan);

const Auth = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const selectedPlan = parseSignupPlan(searchParams.get("plan"));
  const intent = searchParams.get("intent");
  const mode = searchParams.get("mode"); // "sign-up" or default to sign-in
  // Self-serve org-native funnel: /sign-up (plus the legacy /signup alias),
  // ?flow=app, or a plan was chosen.
  const isAppFlow = ["/sign-up", "/signup"].includes(location.pathname)
    || searchParams.get("flow") === "app"
    || !!selectedPlan;

  const { user, isLoaded } = useUser();
  const { primaryRole, isPlatform, loading: rolesLoading } = useUserRole();
  const navigate = useNavigate();

  // After auth: Provenance platform staff go straight to the control-plane console
  // (they operate the business, not a tenant workspace); self-serve signups go to
  // onboarding (which creates the org and hands off to in-app billing); everyone
  // else is routed to their role's home.
  useEffect(() => {
    if (user && isLoaded && !rolesLoading) {
      if (isPlatform) {
        navigate("/platform");
      } else if (isAppFlow) {
        if (isPaidPlan(selectedPlan)) {
          // Legacy Basic maps 1:1 to Tech Health (same $299 Technical offering) so
          // it still auto-starts; Pro has no exact bundle and lands on the bundle
          // picker (autostart no-ops on a non-bundle slug) rather than dead-ending.
          const stored = selectedPlan === "basic" ? "tech_health" : (selectedPlan as string);
          sessionStorage.setItem("pendingPlan", stored);
          // Carry the billing interval so the funnel checks out on the right price.
          if (searchParams.get("billing") === "annual") sessionStorage.setItem("pendingBilling", "annual");
          else sessionStorage.removeItem("pendingBilling");
        } else {
          // A later Free selection must not inherit an abandoned paid choice.
          sessionStorage.removeItem("pendingPlan");
          sessionStorage.removeItem("pendingBilling");
        }
        if (intent === "readiness") {
          sessionStorage.setItem("pendingIntent", intent);
        }
        navigate("/onboarding");
      } else if (primaryRole === "internal_admin" || primaryRole === "internal_reviewer") {
        navigate("/admin");
      } else {
        navigate("/app");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoaded, primaryRole, isPlatform, rolesLoading]);

  const isSignUp = isAppFlow || mode === "sign-up";
  const isAnnual = searchParams.get("billing") === "annual";
  const tierInfo = selectedPlan === "free"
    ? { name: "Free", price: "$0" }
    : isBundleKey(selectedPlan)
      ? {
          name: BUNDLE_BY_KEY[selectedPlan].label,
          // Show the interval-correct price the user will actually be charged.
          price: isAnnual
            ? `$${Math.round(BUNDLE_BY_KEY[selectedPlan].annual / 12)}`
            : `$${BUNDLE_BY_KEY[selectedPlan].monthly}`,
        }
      : selectedPlan && selectedPlan !== "enterprise"
        ? STRIPE_TIERS[selectedPlan]
        : null;
  // Preserve the plan/flow across Clerk's post-auth redirect.
  const returnUrl = location.pathname + location.search;

  // User is authenticated — show a spinner while we resolve roles and redirect
  if (user && isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      {/* Background effects */}
      <div className="absolute inset-0 grid-pattern opacity-5" />
      <div className="absolute inset-0 radial-gradient-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary">
            <Shield className="h-7 w-7 text-primary-foreground" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight">
            PROVENANCE
          </span>
        </div>

        {/* Selected plan badge */}
        {tierInfo && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-4"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border bg-primary/10 border-primary/30 text-primary">
              <span className="font-medium">{tierInfo.name} Plan — {tierInfo.price}/mo</span>
            </span>
          </motion.div>
        )}

        {/* Clerk auth component */}
        <div className="flex justify-center">
          {isSignUp ? (
            <SignUp
              routing="hash"
              afterSignUpUrl={returnUrl}
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "bg-surface-dark border border-border rounded-2xl shadow-xl",
                },
              }}
            />
          ) : (
            <SignIn
              routing="hash"
              afterSignInUrl={returnUrl}
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "bg-surface-dark border border-border rounded-2xl shadow-xl",
                },
              }}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;
